import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Workstation {
  id: string;
  name: string;
  code: string;
  description?: string;
  readyCount: number;
  inProgressCount: number;
  ready_count?: number;
  in_progress_count?: number;
}

export default function ShippingStationsPage() {
  const [stations, setStations] = useState<Workstation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const loadStations = useCallback(async () => {
    try {
      const data = await api.getShippingWorkstations();
      setStations(data);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load workstations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStations();
    const interval = setInterval(loadStations, 15000);
    return () => clearInterval(interval);
  }, [loadStations]);

  const getReadyCount = (s: Workstation) => s.readyCount ?? s.ready_count ?? 0;
  const getInProgressCount = (s: Workstation) => s.inProgressCount ?? s.in_progress_count ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Shipping Stations</h1>
          <p className="text-gray-400 mt-1">Select a workstation to view its queue</p>
        </div>
        <button
          onClick={loadStations}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {stations.length === 0 && !error ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Stations Found</h3>
          <p className="text-gray-400">No shipping workstations are configured yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stations.map((station) => {
            const readyCount = getReadyCount(station);
            const inProgressCount = getInProgressCount(station);
            const totalCount = readyCount + inProgressCount;

            return (
              <button
                key={station.id}
                onClick={() => navigate(`/stations/${station.id}`)}
                className="bg-gray-800 rounded-xl border border-gray-700 p-6 text-left hover:border-indigo-500 hover:bg-gray-750 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-white group-hover:text-indigo-400 transition-colors">
                      {station.name}
                    </h2>
                    <p className="text-gray-500 text-sm font-mono mt-1">{station.code}</p>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gray-700 group-hover:bg-indigo-900/50 transition-colors">
                    <svg className="w-6 h-6 text-gray-400 group-hover:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>

                {station.description && (
                  <p className="text-gray-400 text-sm mb-4">{station.description}</p>
                )}

                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-900/50 text-blue-300 border border-blue-700">
                    {readyCount} Ready
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-900/50 text-yellow-300 border border-yellow-700">
                    {inProgressCount} In Progress
                  </span>
                  {totalCount === 0 && (
                    <span className="text-gray-500 text-sm">No work orders</span>
                  )}
                </div>

                <div className="mt-4 flex items-center text-gray-500 text-sm group-hover:text-indigo-400 transition-colors">
                  <span>View Queue</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
